import { DatasetType } from './apispec';

const ImageSequenceType = 'image-sequence';
const VideoType = 'video';
const MultiType = 'multi';
const LargeImageType = 'large-image';

const MediaTypes: Record<DatasetType, string> = {
  // friendly media type names
  [ImageSequenceType]: 'image sequence',
  [VideoType]: 'video',
  [MultiType]: 'multi',
  [LargeImageType]: 'tiled image',
};

const DefaultVideoFPS = -1;
const FPSOptions = [
  { text: 1, value: 1 },
  { text: 5, value: 5 },
  { text: 10, value: 10 },
  { text: 15, value: 15 },
  { text: 24, value: 24 },
  { text: 25, value: 25 },
  { text: 30, value: 30 },
  { text: 50, value: 50 },
  { text: 60, value: 60 },
  { text: 'Video FPS', value: -1 },
];

const itemsPerPageOptions = [20, 50, 100];

const websafeVideoTypes = [
  'video/mp4',
  'video/webm',
];

const otherVideoTypes = [
  /* avi */
  'video/avi',
  'video/msvideo',
  'video/x-msvideo',
  'video/x-ms-wmv',
  /* mov */
  'video/quicktime',
  /* mpeg */
  'video/mpeg',
  'video/x-mpeg',
  'video/x-mpeq2a',
  /* ogg */
  'video/ogg',
  /* flv */
  'video/x-flv',
];

const calibrationFileTypes = [
  'cam',
  'json',
  'npz',
  'yml',
  'zip',
];

// Optional per-dataset metadata file (e.g. sea-lion flight log) that is stored
// alongside the dataset and handed to opt-in pipelines at run time.
const metadataFileTypes = [
  'json',
  'txt',
  'csv',
];

const fileVideoTypes = [
  'mp4',
  'webm',
  'avi',
  'mov',
  'wmv',
  'mpg',
  'mpeg',
  'mp2',
  'ogg',
  'flv',
];

const largeImageTypes = [
  'image/geotiff',
  'image/tiff',
  'image/tif',
  'image/x-tiff',
  'image/nitf',
  'image/ntf',
];

/** Extension-only formats for basic image sequences (aligned with server validImageFormats). */
const basicImageFileExtensions = [
  'png',
  'jpg',
  'jpeg',
  'sgi',
  'bmp',
  'pgm',
];

/** Extension-only formats for large-image uploads (aligned with server validLargeImageFormats). */
const largeImageFileExtensions = [
  'nitf',
  'tif',
  'tiff',
  'ntf',
  'vrt',
  'r0',
  'r1',
  'r2',
  'r3',
  'r4',
  'r5',
  'r6',
];

/** Desktop Electron open-dialog extensions (GeoTIFF/TIFF only; tiles served via geotiff.js). */
const largeImageDesktopTypes = [
  'geotiff',
  'tiff',
  'tif',
];

/** MIME types and dotted extensions for HTML file input accept on web. */
const largeImageWebAccept = [
  ...largeImageTypes,
  ...largeImageFileExtensions.map((ext) => `.${ext}`),
].join(',');

/** Dotted extensions for desktop-mode file inputs in the web upload UI. */
const largeImageDesktopAccept = largeImageDesktopTypes.map((ext) => `.${ext}`).join(',');

function getLargeImageFileAccept(): string {
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('Electron')) {
    return largeImageDesktopAccept;
  }
  return largeImageWebAccept;
}

function getLargeImageAllowedExtensions(): string[] {
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('Electron')) {
    return largeImageDesktopTypes;
  }
  return largeImageFileExtensions;
}

const websafeImageTypes = [
  // 'image/apng',
  // 'image/bmp',
  'image/gif',
  'image/jpeg',
  'image/png',
  // 'image/svg+xml',
  // 'image/webp',
];

/** Dotted extensions for HTML file-input accept (multi-dot names like a.b.c.png need these). */
const websafeImageFileExtensions = [
  'gif',
  'jpg',
  'jpeg',
  'png',
];

const otherImageTypes = [
  'image/avif',
  'image/tiff',
  'image/bmp',
  'image/x-windows-bmp',
  'image/sgi',
  'image/x-portable-graymap',
];

const otherImageFileExtensions = [
  'avif',
  'tif',
  'tiff',
  'bmp',
  'sgi',
  'pgm',
];

/** MIME types plus dotted extensions for image-sequence file inputs. */
function getImageSequenceFileAccept(): string {
  return [
    ...websafeImageFileExtensions.map((ext) => `.${ext}`),
    ...otherImageFileExtensions.map((ext) => `.${ext}`),
    ...websafeImageTypes,
    ...otherImageTypes,
  ].join(',');
}

const inputAnnotationTypes = [
  'application/json',
  'text/csv',
  'text/yaml',
  'application/x-yaml',
];

const inputAnnotationFileTypes = [
  'yml',
  'yaml',
  'json',
  'csv',
];

const listFileTypes = [
  'txt',
];

/**
 * Per-camera registration transform files: DIVE registration .json (the
 * registration panel's save format).
 */
const transformFileTypes = [
  'json',
];

const zipFileTypes = [
  'zip',
];

const stereoPipelineMarker = 'stereo';
/** Girder item meta key marking the original stereoscopic calibration upload (pipeline input). */
const calibrationFileMarker = 'calibrationFile';
/** Girder item meta key marking the JSON camera-rig used for calibration display. */
const jsonCalibrationFileMarker = 'jsonCalibrationFile';
/** Girder item meta key marking a frame-metadata attachment for Girder UI. */
const frameMetadataFileMarker = 'frameMetadata';
/** Pipeline name/category substrings hidden from the web run-pipeline menu. */
const webExcludedPipelineTerms = ['seagis'];
const multiCamPipelineMarkers = ['2-cam', '3-cam'];
const pipelineCreatesDatasetMarkers = ['transcode', 'filter'];

const JsonConfigRegEx = /^.*\.?(meta|config)\.json$/;
/**
 * A KWCOCO species list: the classes a dataset may use, travelling beside the media as
 * configuration rather than annotations. Deliberately not folded into JsonConfigRegEx,
 * which also names the portable config.json that identifies an exported dataset
 * directory and supplies a dataset's own metadata on import.
 */
const JsonSpeciesRegEx = /^.*\.?species\.json$/i;

function simplifyTrainingName(item: string) {
  return item.replace('.conf', '');
}

export {
  DefaultVideoFPS,
  ImageSequenceType,
  VideoType,
  LargeImageType,
  MediaTypes,
  MultiType,
  FPSOptions,
  itemsPerPageOptions,
  calibrationFileTypes,
  metadataFileTypes,
  fileVideoTypes,
  otherImageTypes,
  otherImageFileExtensions,
  otherVideoTypes,
  websafeImageTypes,
  websafeImageFileExtensions,
  websafeVideoTypes,
  getImageSequenceFileAccept,
  inputAnnotationTypes,
  largeImageTypes,
  basicImageFileExtensions,
  largeImageFileExtensions,
  largeImageDesktopTypes,
  largeImageWebAccept,
  largeImageDesktopAccept,
  getLargeImageFileAccept,
  getLargeImageAllowedExtensions,
  inputAnnotationFileTypes,
  listFileTypes,
  transformFileTypes,
  zipFileTypes,
  stereoPipelineMarker,
  calibrationFileMarker,
  jsonCalibrationFileMarker,
  frameMetadataFileMarker,
  webExcludedPipelineTerms,
  multiCamPipelineMarkers,
  pipelineCreatesDatasetMarkers,
  JsonConfigRegEx,
  JsonSpeciesRegEx,
  simplifyTrainingName,
};
